import React, { useEffect } from 'react';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from "react-redux";
import { useFeatureEnabled } from 'Components/FeatureToggle';
import { useRealtimeTags } from 'Core/hooks';
//Helpers
import { GetFormatForMoment } from 'Core/data/Helpers';
import { NotificationManager } from 'Components/Notifications';
// Components
import { Grid, Grow, Container, Typography } from "@mui/material";
import { Table, SearchInput, Toolbar, Button } from 'Components/uiControls';
import { MenuComponent } from 'Components/Menu';
import { TableButtons } from 'Components/uiControls/Table';
import { TagsGroup } from 'Components/Tag';
import colors from 'Assets/sass/core/vars.scss';
import { HasPermission } from "Core/security"
// Actions
import {
  catalogLoad,
  sendGenericExport,
  clearRedux
} from "Redux/actions";

import { openImportSubUserModal, setImportNotificationSubUser } from '../importSubUserModal/redux/slice';

//Hooks
import { useImportNotifications } from 'Core/hooks/useImportNotifications'
import ImportResultsModal from '../../../../../../core/views/importResultsModal';

const TableSubUser = (props) => {
  const { messages, locale } = useIntl();
  const { dateFormat, onActions, setSelectedItems, tableRef, pagination, loadingSubUser } = props;

  // Hooks
  const dispatch = useDispatch();

  // State manager
  const {
    registers,
    allfilters,
    loading,
    total
  } = useSelector(
    (state) => state.subUserRedux
  );
  const tags = useRealtimeTags({ trackFields: ['id', 'label', 'disabled', 'tagPath', 'tags'] });
  const exportResult = useSelector(state => state.commomRedux.exportResult);
  const exportName = useSelector(state => state.commomRedux.exportName);
  const refreshUsersTab = useSelector(state => state.catalogsRedux.refreshUsersTab);
  const importNotification = useSelector(state => state.subUsersImportReducer?.importNotification);
  const userFormat = GetFormatForMoment(dateFormat);

  useImportNotifications(
    state => state.subUsersImportReducer,
    setImportNotificationSubUser
  );

  // Permissions
  const user = useSelector((state) => state.securityRedux.user);
  const editablePermissions = HasPermission(user, "", [331]);
  const canImportUsers = useFeatureEnabled('bulk-import-enabled', false);

  useEffect(() => {
    dispatch(catalogLoad(['tags']));
  }, [])

  useEffect(() => {
    if (registers?.length) {
      pagination.setSelectedItems([]);
    }
  }, [registers])

  useEffect(() => {
    setSelectedItems(pagination?.selectedItems);
  }, [pagination?.selectedItems])

  useEffect(() => {
    if (total != undefined) {
      pagination.setTotal(total);
    }
  }, [total]);

  useEffect(() => {
    if (refreshUsersTab) {
      dispatch(pagination.resetTable());
      dispatch(clearRedux("CATALOG_REDUX", { isRefreshAvClips: null }));
    }
  }, [refreshUsersTab])

  useEffect(() => {
    if (importNotification?.status === "SUCCESS" ||
      importNotification?.status === "PARTIAL_SUCCESS") {
      // Refresh the table data
      pagination.onChangePage();

      // Clear the notification to prevent infinite loop
      dispatch(setImportNotificationSubUser(null));
    }
  }, [importNotification?.status])

  // Handle push notification for completed import
  /*
  useEffect(() => {
    if (importNotification?.transactionId && importNotification?.importType === 'ImportUsers') {
      const { status, failureCount, message } = importNotification;

      // Show success or warning based on status/failures
      const notificationType = (failureCount > 0 || status === 'PARTIAL' || status === 'FAILED') ? 'warning' : 'success';

      NotificationManager[notificationType](
        message || messages['importSubUser_completed'],
        messages['importSubUser_title'],
        6000,
        null,
        null,
        notificationType
      );

      // Refresh the table data
      pagination.onChangePage();

      // Clear the notification to prevent infinite loop
      dispatch(setImportNotificationSubUser(null));
    }
  }, [importNotification, messages, pagination, dispatch])
  */
 
  useEffect(() => {
    pagination.setColumns([
      {
        key: 'id',
        title: messages['subUser_id'],
        show: false,
        exportOnly: {csv: true, pdf: false},
        export: true,
        exportValue: (row) => row?.id,
        functionString: 'return row?.id'
      },
      {
        key: 'name',
        title: messages['subUser_name'],
        show: true,
        export: false,
        width: 250,
        render: (row) => {

          const isExpired = moment(row?.expiration).isSameOrBefore(moment());
          const options = [messages['edit'], messages['delete']];

          if (!isExpired) {
            options.push(messages['subUser_resetEmail']);

            if (row.email && row.registrationStatus === 'Active') {
              options.push(messages['subUser_resetPassword']);
            }

            if (['Pending', 'Expired'].includes(row.registrationStatus)) {
              options.push(messages['subUser_resendInvite']);
            }
          }

          return (
            <Grid
              container
              alignItems="center"
              justifyContent="flex-start"
              className="context-menu-container"
              wrap="nowrap"
            >
              <Grid item zeroMinWidth>
                {row?.firstName ? `${row?.firstName} ${row?.lastName}` : ''}
              </Grid>
              {editablePermissions && 
                <Grid item>
                  <MenuComponent
                    disabled={loading}
                    options={options}
                    icon={'more_vert'}
                    className="context-menu"
                    onClick={(type, value) => onActions(type, value)}
                    value={row}
                  />
                </Grid>
              }
            </Grid>
          )
        }
      },
      {
        key: 'firstName',
        title: messages['subUser_firstName'],
        show: false,
        exportOnly: true,
        export: true,
        exportValue: (row) => row?.firstName || '',
        functionString: 'return row?.firstName || ""'
      },
      {
        key: 'lastName',
        title: messages['subUser_lastName'],
        show: false,
        exportOnly: true,
        export: true,
        exportValue: (row) => row?.lastName || '',
        functionString: 'return row?.lastName || ""'
      },
      {
        key: 'userName',
        title: messages['subUser_userName'],
        show: true,
        exportOnly: true,
        export: true,
        render: (row) => {
          return (
            <Grid
              container
              alignItems="center"
              justifyContent="space-between"
              className="context-menu-container"
              wrap="nowrap"
            >
              <Grid item xs zeroMinWidth>
                <Typography>{row?.userName}</Typography>
              </Grid>
            </Grid>
          )
        },
        exportValue: (row) => row?.userName,
        functionString: 'return row?.userName'
      },
      {
        key: 'email',
        title: messages['subUser_email'],
        show: true,
        export: true,
        render: (row) => <span>{row?.email ? row?.email : messages['none']}</span>,
        exportValue: (row) => row?.email ? row?.email : messages['none'],
        functionString: 'return row?.email ? row?.email : ""'
      },
      {
        key: 'registrationStatus',
        title: messages['subUser_registrationStatus'],
        show: true,
        export: true,
        render: (row) => <span>{row?.registrationStatus || messages['none']}</span>,
        exportValue: (row) => row?.registrationStatus || messages['none'],
        functionString: 'return row?.registrationStatus ? row?.registrationStatus : ""'
      },
      {
        key: 'phone',
        title: messages['subUser_phone'],
        show: true,
        export: true,
        render: (row) => <span>{row?.phone ? row?.phone : messages['none']}</span>,
        exportValue: (row) => row?.phone ? row?.phone : messages['none'],
        functionString: 'return row?.phone ? row?.phone : "' + messages['none'] + '"'
      },
      {
        key: 'tags',
        title: messages['subUser_tags'],
        show: true,
        export: true,
        render: (row) => {
          if (!row?.tagIds) return "";
          let tagIds = row?.tagIds?.split(",");
          let userTags = []
          if (tagIds?.includes("-99") || tagIds?.includes(-99)) {
            userTags = [{ id: -99, label: messages['tagPicker_allTagsAreSelected'], color: colors?.blue_gtc }];
          } else {
            userTags = tags?.filter(tag => tagIds?.includes(`${tag?.id}`));
          }

          return (
            <Grid
              container
              alignItems={'center'}
              justifyContent={'space-between'}
              style={{ width: 100 }}
            >
              <TagsGroup
                options={(userTags || []).map(ut => {
                  return {
                    ...ut,
                    label: ut?.id == -1 ? messages[ut?.label] : ut?.label
                  }
                })}
                nameField="label"
                colorField="color"
              />
            </Grid>
          )
        },
        exportValue: (row) => {
          if (!row?.tagIds) return "";
          let tagIds = row?.tagIds?.split(",");
          if (tagIds?.includes("-99") || tagIds?.includes(-99)) {
            return messages['tagPicker_allTagsAreSelected'];
          }
          // Use row.tags if available, otherwise filter from catalog
          if (row?.tags && Array.isArray(row.tags)) {
            return row?.tags?.map(item => item?.label).join(" / ");
          }
          let filterTags = tags?.filter(tag => tagIds?.includes(`${tag?.id}`));
          return filterTags?.map((item) => item?.label).join(" / ");
        },
        functionString: 'var tagIds = row?.tagIds?.split(","); return (tagIds?.includes("-99") || tagIds?.includes(-99)) ? "'+ messages['tagPicker_allTagsAreSelected'] +'" : row?.tags?.map(item => item?.label).join(" / ")'
      },
      {
        key: 'role',
        title: messages['subUser_role'],
        show: true,
        export: true,
        render: (row) => {
          let labelPermissions = row?.customRoleId ? messages['permissions_custom'] : row?.role?.name
          return (
            <Grid
              container
              alignItems={'center'}
              justifyContent={'space-between'}
            >
              <span>{labelPermissions}</span>
            </Grid>
          )
        },
        exportValue: (row) => row?.customRoleId ? messages['permissions_custom'] : row?.role?.name,
        functionString: 'return row?.customRole ? "custom" : row?.role?.name'
      },
      {
        key: 'expiration',
        title: messages['subUser_expiryDate'],
        show: true,
        export: true,
        render: (row) => {
          return (
            <Grid
              container
              alignItems={'center'}
              justifyContent={'space-between'}
            >
              <span>{
                moment(row?.expiration).isValid() ?
                  moment(row?.expiration).utc().format(GetFormatForMoment(dateFormat)) :
                  messages["subUser_never"]
              }</span>
            </Grid>
          )
        },
        exportValue: (row) => {
          return row?.expiration != '0000-00-00' && moment(row?.expiration).isValid() ? moment(row?.expiration).utc().format(GetFormatForMoment(dateFormat)) : messages["subUser_never"]
        },
        functionString: `return (row?.expiration && row?.expiration !== '0000-00-00' && moment(row?.expiration).isValid()) ? moment(row?.expiration).utc().format('${userFormat}') : '${messages["subUser_never"]}';`
      }
    ]);
    pagination.onChangePage();
  }, [locale, tags?.length]);

  useEffect(() => {
    if (exportResult && exportName === "subusers") {
      NotificationManager.info(
        messages['exportInProcessBody'],
        messages['exportInProcessTitle'],
        4000,
        null,
        null,
        'info',
        null
      );
      dispatch(clearRedux("COMMON", { exportResult: null, exportName: null }));
    }
  }, [exportResult, exportName]);

  return (
    <Container maxWidth={false}>
      <Grid container direction="row" justifyContent="space-between" alignItems="center">
        <Grid item xs={6} sm={6}>
          <Toolbar>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="center"
            >
              {editablePermissions && 
                <Grid item style={{ marginLeft: 6 }}>
                  <Button
                    type="icon"
                    icon="add_box"
                    placement="bottom"
                    tooltip={messages['add']}
                    onClick={() => onActions(messages['create'])}
                    disabled={loadingSubUser}
                  />
                </Grid>
              }
              <Grid item>
                <TableButtons
                  messages={messages}
                  tableRef={tableRef}
                  {...pagination}
                  buttons={['export']}
                  onExport={(type) => {
                    // Define the correct export column order
                    const exportColumnOrder = ['id', 'firstName', 'lastName', 'userName', 'email', 'registrationStatus', 'phone', 'tags', 'role', 'expiration'];

                    if (type === "Pdf") {
                      exportColumnOrder.splice(exportColumnOrder.indexOf('id'), 1); // Remove ID from PDF export
                    }
                    
                    // Use pagination columns which should match our source definition
                    const columns = pagination?.columns || tableRef.current?.columns;
                    
                    // Build headers in the correct order
                    const headers = [];
                    exportColumnOrder.forEach(keyName => {
                      const col = columns?.find(x => x.key === keyName);
                      if (col && (col.show || col.exportOnly) && col.export) {
                        headers.push({ header: col.title, column: col.key, functionString: col.functionString });
                      }
                    });

                    if (pagination?.selectedItems?.length) {
                        if (type === "Csv") {
                          tableRef.current.exportCSV(headers);
                        } else if (type === "Pdf") {
                          tableRef.current.exportPDF(headers);
                        }
                    } else {

                      dispatch(sendGenericExport(
                        "subusers",
                        type,
                        `
                        query searchSubUsers ($openSearch: String, $limit: Int, $offset: Int) {
                          subusers: searchSubUsers (openSearch: $openSearch, limit: $limit, offset: $offset) {
                              items {
                                  id
                                  userName
                                  firstName
                                  lastName
                                  roleId
                                  customRoleId
                                  role {
                                      name
                                  }
                                  customRole {
                                      id
                                      name
                                  }
                                  tagIds
                                  tags {
                                    id
                                    label
                                  }
                                  email
                                  expiration
                                  phone
                                  registrationStatus
                              }
                              total
                          }
                        }
                        `,
                        { openSearch: pagination?.searchText },
                        headers,
                        { unit: 'px', size: 'A4', orientation: 'landscape', fontSize: 10 },
                        [],
                        false,
                        false
                      ));
                    }
                  }}
                />
              </Grid>
              {editablePermissions &&
                <Grid item style={{ marginLeft: 6 }}>
                <Button
                  key="import-users-button"
                  type="icon"
                  icon="ios_share"
                  size="small"
                  tooltip={messages['subUser_import']}
                  onClick={() => {
                    dispatch(openImportSubUserModal(true));
                  }}
                />
              </Grid>
              }
              {
                editablePermissions && pagination?.selectedItems?.length > 0 &&
                <Grid item>
                  <Button
                    type="icon"
                    icon="delete"
                    placement="bottom"
                    tooltip={messages['delete']}
                    disabled={!pagination?.selectedItems?.length}
                    onClick={() => onActions(messages['delete'])}
                  />
                </Grid>
              }
            </Grid>
          </Toolbar>
        </Grid>
        <Grid item xs={12} sm={6} className="mb-10 text-right">
          <SearchInput
            placeholder={messages['search']}
            {...pagination}
          />
        </Grid>
      </Grid>
      <Table
        ref={tableRef}
        keyColumn="userName"
        loading={loading}
        topheight={235}
        data={registers}
        exportOrientation='landscape'
        exportName='account-users'
        {...pagination}
        hideDragable
      />
      <ImportResultsModal />
    </Container>
  );
}

export default TableSubUser;